/**
 * Phase 20 / Task 20.7 — §80 "Templates": Simple and Full per entity, plus the
 * multi-entity workbook. The workbooks themselves (README sheet, header
 * comments, enum dropdowns) are generated by `GET /v1/import-templates/…`
 * (task 20.0); this panel only lists and downloads them.
 *
 * Downloaded through `authedFetch` and a blob URL rather than a plain link,
 * because the route needs the bearer token a plain `<a href>` cannot carry.
 */

import * as React from 'react';
import { authedFetch, useApiOpts, type WebApiOpts } from '@lodex-pro/web-shared';

import { readErrorMessage } from './api';
import { useTemplateCatalog } from './templates-query';
import { ActionButton, Notice, Panel, muted } from './ui';

type Variant = 'simple' | 'full';

function fileNameFrom(res: Response, fallback: string): string {
  const cd = res.headers.get('content-disposition') ?? '';
  const m = /filename="([^"]+)"/.exec(cd);
  return m?.[1] ?? fallback;
}

async function downloadTemplate(opts: WebApiOpts, path: string, fallback: string): Promise<void> {
  const res = await authedFetch(opts, path);
  if (!res.ok) throw new Error((await readErrorMessage(res)).message);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = fileNameFrom(res, fallback);
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next tick, not synchronously: some browsers start reading
  // the blob only after click() returns, and a revoked URL downloads nothing.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function TemplatesPanel(): React.JSX.Element {
  const apiOpts = useApiOpts();
  const query = useTemplateCatalog();
  const [busy, setBusy] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const get = (id: string, path: string, fallback: string): void => {
    setBusy(id);
    setError(null);
    downloadTemplate(apiOpts, path, fallback)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Download failed.'))
      .finally(() => setBusy(null));
  };

  const pair = (id: string, path: (v: Variant) => string, fallback: string): React.JSX.Element => (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {(['simple', 'full'] as const).map((v) => (
        <ActionButton
          key={v}
          disabled={busy !== null}
          aria-label={`Download ${v} template: ${fallback}`}
          onClick={(): void => get(`${id}-${v}`, path(v), `${fallback}-${v}.xlsx`)}
        >
          {busy === `${id}-${v}` ? 'Preparing…' : v === 'simple' ? 'Simple' : 'Full'}
        </ActionButton>
      ))}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Panel labelledBy="import-templates-heading">
        <h2 id="import-templates-heading" style={{ fontSize: '18px', fontWeight: 600 }}>
          Templates
        </h2>
        <p style={{ ...muted, marginTop: 8 }}>
          Simple has the required columns only. Full has every column. Each header has a note
          explaining what goes in it, and list columns have a dropdown.
        </p>
      </Panel>
      {error !== null && (
        <Notice tone="error" role="alert">
          {error}
        </Notice>
      )}
      <Panel>
        <h3 style={{ fontSize: 16, fontWeight: 600 }}>Everything in one workbook</h3>
        <p style={{ ...muted, margin: '4px 0 12px' }}>
          Every sheet, plus a README explaining how the sheets refer to each other.
        </p>
        {pair('all', (v) => `/v1/import-templates/workbook?variant=${v}`, 'all-entities')}
      </Panel>
      {query.isError && (
        <Notice tone="error" role="alert">
          {query.error.message}
        </Notice>
      )}
      {(query.data ?? []).map((t) => (
        <Panel key={t.key}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{t.sheetName}</h3>
          <p style={{ ...muted, margin: '4px 0 12px' }}>{t.purpose}</p>
          {pair(
            t.key,
            (v) => `/v1/import-templates/${encodeURIComponent(t.key)}?variant=${v}`,
            t.sheetName.toLowerCase().replace(/\s+/g, '-'),
          )}
        </Panel>
      ))}
    </div>
  );
}
